import Spinner from "../common/Spinner";
import CheckIcon from "@/assets/icons/check.svg?react";
import { blobToDataUri, blobToFile } from "@/shared/utils/file";
import { FC, memo, useCallback, useEffect, useState, useRef } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import CloseIcon from "@/assets/icons/close.svg?react";

type OwnProps = {
  file?: Blob;
  onChange: (file: File) => void;
  onClose: () => void;
};

// Change to 'base64' to get base64-encoded string
const cropperResultOptions: Croppie.ResultOptions & { type: "blob" } = {
  type: "blob",
  quality: 1,
  format: "jpeg",
  circle: false,
  size: { width: 1024, height: 1024 },
};

type ICroppie = typeof import("croppie");
let Croppie: ICroppie;
let croppiePromise: Promise<{ default: ICroppie }>;

async function ensureCroppie() {
  if (!croppiePromise) {
    croppiePromise = import("../../shared/lib/croppie") as unknown as Promise<{
      default: ICroppie;
    }>;
    Croppie = (await croppiePromise).default;
  }

  return croppiePromise;
}

const CropModal: FC<OwnProps> = ({ file, onChange, onClose }: OwnProps) => {
  const [isCroppieReady, setIsCroppieReady] = useState(false);
  const [inited, setInited] = useState(false);
  const cropperRef = useRef<Croppie | null>(null);

  const initCropper = useCallback(async (imgFile: Blob) => {
    try {
      const cropContainer = document.getElementById("avatar-crop");
      if (!cropContainer) {
        return;
      }

      const imgEl = cropContainer.querySelector("img");
      if (imgEl) {
        imgEl.onerror = () => {
          imgEl.style.display = "none";
        };
      }

      // Destroy existing cropper if it exists
      if (cropperRef.current) {
        cropperRef.current.destroy();
        cropperRef.current = null;
      }

      const { offsetWidth, offsetHeight } = cropContainer;

      cropperRef.current = new Croppie(cropContainer, {
        enableZoom: true,
        boundary: {
          width: offsetWidth,
          height: offsetHeight,
        },
        viewport: {
          width: offsetWidth - 16,
          height: offsetHeight - 16,
          type: "circle",
        },
      });

      const dataUri = await blobToDataUri(imgFile);
      await cropperRef.current.bind({ url: dataUri });
      setInited(true);
    } catch (error) {
      console.error("Failed to initialize cropper:", error);
      setInited(false);
    }
  }, []);

  useEffect(() => {
    if (!file) {
      return;
    }

    if (!isCroppieReady) {
      ensureCroppie().then(() => setIsCroppieReady(true));
      return;
    }

    const timeout = setTimeout(() => {
      initCropper(file);
    }, 0);

    return () => {
      clearTimeout(timeout);
    };
  }, [file, isCroppieReady, initCropper]);

  // Cleanup cropper when component unmounts or file changes
  useEffect(() => {
    return () => {
      if (cropperRef.current) {
        cropperRef.current.destroy();
        cropperRef.current = null;
      }
    };
  }, []);

  // Also cleanup when dialog closes
  useEffect(() => {
    if (!file && cropperRef.current) {
      cropperRef.current.destroy();
      cropperRef.current = null;
      setInited(false);
    }
  }, [file]);

  const handleCropClick = useCallback(async () => {
    if (!cropperRef.current) {
      return;
    }

    try {
      const result: Blob | string = await cropperRef.current.result(
        cropperResultOptions
      );
      const croppedImg =
        typeof result === "string" ? result : blobToFile(result, "avatar.jpg");

      onChange(croppedImg);
      onClose();
    } catch (error) {
      console.error("Failed to crop image:", error);
    }
  }, [onChange, onClose]);

  return (
    <Dialog open={Boolean(file)} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="border-none outline-none size-full pb-safe-bottom px-0 flex-center">
        <button
          type="button"
          className="absolute z-50 outline-none border-none bg-soft rounded-full size-8 flex-center top-safe-top mt-3 right-4"
          onClick={onClose}
        >
          <CloseIcon className="size-5" />
        </button>
        <div className="cropper-dialog">
          <DialogHeader>
            <DialogTitle className="hidden" />
          </DialogHeader>

          {isCroppieReady && <div id="avatar-crop" className="mx-auto" />}
          {!inited && (
            <div className="absolute inset-0 flex-center">
              <Spinner className="size-8" />
            </div>
          )}
          <div className="absolute bottom-safe-bottom mb-3 right-4">
            <button
              type="button"
              className="p-2 rounded-full bg-blue"
              onClick={handleCropClick}
            >
              <CheckIcon className="size-5" />
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default memo(CropModal);
